import React from 'react';
import { Option } from 'lib/types';
import {
    CHANNEL_OPTIONS,
    DAYS_OF_WEEK_OPTIONS,
    EActionTypeGroup,
    getActionTypeGroup,
    MONTH_OF_YEAR_OPTIONS,
    NUMBER_PERIOD_OPTIONS,
    PRIMARY_PLATFORM_OPTIONS,
    SCENARIO_OPTIONS,
    TIME_OF_DAY_OPTIONS
} from 'components/audience/models/addTriggersModel';
import { Box, Button, Grid, IconButton, InputLabel, styled, Typography } from '@mui/material';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { IAudienceAddTriggersFormData } from 'components/audience/forms/AudienceTriggersBaseForm';
import { Condition } from '@pepperhq/types-sdk';
import { isArrayOf, isString } from 'lib/typeguards';
import { CreatableChipInputFormField } from 'lib/form/fields/CreatableChipInputFormField';
import { MuiModal } from 'ui/MuiModal';
import { Column, Row } from 'ui/Flex';
import { useFormikContext } from 'formik';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { CreatableChipInput } from 'lib/form/fields/CreatableChipInput';
import { DatePickerFormField } from 'lib/form/fields/DatePickerFormField';
import { format } from 'date-fns';
import { AllConditionSubjects } from 'components/rules/rules.types';
import { SubscriptionScheme } from 'components/subscription-schemes/subscriptionSchemeModel';

function getFavouriteLocationsCondition(favouriteLocationIds?: string[]): undefined | Condition {
    if (!Array.isArray(favouriteLocationIds) || favouriteLocationIds.length === 0) {
        return undefined;
    }
    if (favouriteLocationIds.length === 1) {
        return {
            subject: 'ANY(metadata.favouriteLocations)',
            predicate: 'EQUAL',
            object: favouriteLocationIds.at(-1)
        };
    }
    return {
        subject: 'ANY(metadata.favouriteLocations)',
        predicate: 'IN',
        object: favouriteLocationIds
    };
}

export const renderTimeOfDaySelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Time of the day</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={TIME_OF_DAY_OPTIONS}
                    name="dayPart"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDayOfWeekSelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Day of the week</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={DAYS_OF_WEEK_OPTIONS}
                    name="dayOfWeek"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicDateRangeSelect = (nameFrom: string, nameTo: string, label: string) => (
    <Grid size={12}>
        <Grid container alignItems="center" spacing={1}>
            <Grid size={4}>
                <InputLabel>{label}</InputLabel>
            </Grid>
            <Grid size={4}>
                <DatePickerFormField label="after" name={nameFrom} disableOpenPicker />
            </Grid>
            <Grid size={4}>
                <DatePickerFormField label="before" name={nameTo} disableOpenPicker />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicDaysOfWeekSelect = (name = 'dayOfWeek', label = 'Days of the week') =>
    renderDynamicMultiSelect(name, label, DAYS_OF_WEEK_OPTIONS);

export const renderWeekOfYearInput = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Week of the year</InputLabel>
            </Grid>
            <Grid size={8}>
                <NumberFormField name="weekOfYear" />
            </Grid>
        </Grid>
    </Grid>
);

export const renderMonthOfYearSelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Month of the year</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={MONTH_OF_YEAR_OPTIONS}
                    name="monthOfYear"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderPrimaryPlatformSelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Primary platform</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={PRIMARY_PLATFORM_OPTIONS}
                    name="primaryPlatform"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicNumberField = (name: string, predicateName: string, label: string) => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>{label}</InputLabel>
            </Grid>
            <Grid size={8}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid size={6}>
                        <SelectFormField
                            displayEmpty
                            native={false}
                            emptyLabel="None"
                            options={NUMBER_PERIOD_OPTIONS}
                            name={predicateName}
                        />
                    </Grid>
                    <Grid size={6}>
                        <NumberFormField name={name} />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    </Grid>
);

export const renderAmountField = () => renderDynamicNumberField('amount', 'amountPredicate', 'Amount');

export const renderScenarioSelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Scenario</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={SCENARIO_OPTIONS}
                    name="scenario"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderChannelSelect = () => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel>Channel</InputLabel>
            </Grid>
            <Grid size={8}>
                <SelectFormField
                    displayEmpty
                    native={false}
                    emptyLabel="None"
                    options={CHANNEL_OPTIONS}
                    name="channel"
                />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicSingleSelect = (
    name: string,
    label: string,
    options: Option[],
    isRequired?: boolean
) => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel required={isRequired}>{label}</InputLabel>
            </Grid>
            <Grid size={8}>
                <AutocompleteFormField options={options} name={name} />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicMultiSelect = (
    name: string,
    label: string,
    options: Option[],
    isRequired?: boolean,
    description?: React.ReactNode
) => (
    <Grid size={12}>
        <Grid container alignItems="center">
            <Grid size={4}>
                <InputLabel required={isRequired}>{label}</InputLabel>
                {description && (
                    <Typography variant="caption" color="textSecondary" fontStyle="italic">
                        {description}
                    </Typography>
                )}
            </Grid>
            <Grid size={8}>
                <AutocompleteFormField multiple options={options} name={name} />
            </Grid>
        </Grid>
    </Grid>
);

export const renderDynamicTextField = (
    name: string,
    label: string,
    isRequired: boolean,
    placeholder?: string,
    description?: React.ReactNode
) => (
    <Grid size={12}>
        <Grid container alignItems="start">
            <Grid size={4}>
                <InputLabel required={isRequired}>{label}</InputLabel>
            </Grid>
            <Grid size={8}>
                <TextFormField name={name} placeholder={placeholder} description={description} />
            </Grid>
        </Grid>
    </Grid>
);

const StyledColumn = styled(Column)(({ theme }) => ({
    gap: theme.spacing(2),
    flex: 1,
    maxHeight: 600,
    overflowY: 'auto',
    width: '500px'
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 500
}));

const StyledRow = styled(Row)(({ theme }) => ({
    marginTop: theme.spacing(5)
}));

type ExpandedChipInputModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (value: string[]) => void;
    value: string[];
    placeholder?: string;
};

const ExpandedChipInputModal = ({
    open,
    onClose,
    onConfirm,
    value,
    placeholder
}: ExpandedChipInputModalProps) => {
    const [values, setValues] = React.useState(value);

    const handleChange = React.useCallback((newValue: string[]) => {
        setValues(newValue);
    }, []);

    const handleConfirm = React.useCallback(() => {
        onConfirm(values);
    }, [onConfirm, values]);
    return (
        <MuiModal open={open} onClose={onClose}>
            <StyledColumn>
                <StyledTitle variant="h6">Edit</StyledTitle>
                <CreatableChipInput
                    value={values}
                    onChange={handleChange}
                    placeholder={placeholder}
                    options={[]}
                    textFieldVariant="outlined"
                />
                <StyledRow flex={1} align="flex-end" gutter>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirm}>
                        Confirm
                    </Button>
                </StyledRow>
            </StyledColumn>
        </MuiModal>
    );
};

type ExpandableDynamicChipInputProps = {
    name: string;
    label: string;
    placeholder?: string;
    isRequired?: boolean;
    isExpandable?: boolean;
};

export const ExpandableDynamicChipInput = ({
    name,
    label,
    placeholder,
    isRequired,
    isExpandable
}: ExpandableDynamicChipInputProps) => {
    const { values, setFieldValue } = useFormikContext<IAudienceAddTriggersFormData>();

    const [isOpen, setIsOpen] = React.useState(false);

    const fieldValues = React.useMemo(
        () => (values[name as keyof IAudienceAddTriggersFormData] as string[]) || [],
        [values, name]
    );

    const handleOpen = React.useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleClose = React.useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleConfirm = React.useCallback(
        (value: string[]) => {
            setIsOpen(false);
            setFieldValue(name, value);
        },
        [setFieldValue, name]
    );

    return (
        <Grid size={12}>
            <Grid container alignItems="start">
                <Grid size={4}>
                    <InputLabel required={isRequired}>{label}</InputLabel>
                </Grid>
                <Grid size={8}>
                    <Row>
                        <CreatableChipInputFormField
                            name={name}
                            placeholder={placeholder}
                            limitTags={4}
                            maxInputHeight={100}
                        />
                        {isExpandable && (
                            <Box display="flex" alignItems="center">
                                <IconButton onClick={handleOpen}>
                                    <OpenInFullIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Row>
                </Grid>
            </Grid>
            {isOpen && (
                <ExpandedChipInputModal
                    open={isOpen}
                    onClose={handleClose}
                    onConfirm={handleConfirm}
                    value={fieldValues}
                    placeholder={placeholder}
                />
            )}
        </Grid>
    );
};

type SubscriptionPricingPlanSelectProps = {
    subscriptionSchemes: SubscriptionScheme[];
};

/**
 * Formats a pricing plan for human-readable display
 * @param price - The price amount in cents/pence
 * @param interval - The billing interval (day, week, month, year)
 * @param intervalCount - The number of intervals
 * @returns Human-readable label like "£9.99 every month" or "£19.99 every 2 weeks"
 */
function formatPricingPlanLabel(price: number, interval: string, intervalCount: number): string {
    const intervalLabels: Record<string, { singular: string; plural: string }> = {
        day: { singular: 'day', plural: 'days' },
        week: { singular: 'week', plural: 'weeks' },
        month: { singular: 'month', plural: 'months' },
        year: { singular: 'year', plural: 'years' }
    };

    const formattedPrice = price.toFixed(2);
    const labels = intervalLabels[interval];

    let intervalLabel: string;
    if (!labels) {
        intervalLabel = `${interval}(s)`;
    } else if (intervalCount === 1) {
        intervalLabel = labels.singular;
    } else {
        intervalLabel = labels.plural;
    }

    const countPrefix = intervalCount > 1 ? `${intervalCount} ` : '';
    return `£${formattedPrice} every ${countPrefix}${intervalLabel}`;
}

export const SubscriptionPricingPlanSelect = ({
    subscriptionSchemes
}: SubscriptionPricingPlanSelectProps) => {
    const { values } = useFormikContext<IAudienceAddTriggersFormData>();

    const pricingPlanOptions = React.useMemo(() => {
        const { subscriptionSchemeId } = values;

        if (!subscriptionSchemeId) {
            // No scheme selected, no pricing plans to show
            return [];
        }

        const selectedScheme = subscriptionSchemes.find(scheme => scheme._id === subscriptionSchemeId);

        if (!selectedScheme || !selectedScheme.pricing || selectedScheme.pricing.length === 0) {
            return [];
        }

        // Generate options from the scheme's pricing plans
        return selectedScheme.pricing
            .filter(pricing => pricing.id)
            .map(pricing => {
                // Use visualisation.name if available, otherwise generate from price/interval/count
                const label =
                    pricing.visualisation?.name ??
                    formatPricingPlanLabel(
                        pricing.price,
                        pricing.billing.interval,
                        pricing.billing.intervalCount
                    );
                return {
                    value: pricing.id,
                    label
                };
            });
    }, [subscriptionSchemes, values]);

    // Don't render if no subscription scheme is selected
    if (!values.subscriptionSchemeId) {
        return null;
    }

    return (
        <Grid size={12}>
            <Grid container alignItems="center">
                <Grid size={4}>
                    <InputLabel>Pricing Plan</InputLabel>
                </Grid>
                <Grid size={8}>
                    <AutocompleteFormField options={pricingPlanOptions} name="stripePriceId" />
                </Grid>
            </Grid>
        </Grid>
    );
};

function createConditionForValueOrArray(subject: AllConditionSubjects, object: string | string[]) {
    if (isArrayOf(isString, object) && object.length > 1) {
        return {
            subject,
            predicate: 'IN',
            object
        } as Condition;
    }
    if (isArrayOf(isString, object) && object.length === 1) {
        return {
            subject,
            predicate: 'EQUAL',
            object: object[0]
        } as Condition;
    }
    if (isString(object)) {
        return {
            subject,
            predicate: 'EQUAL',
            object
        } as Condition;
    }
}

export const makeAudienceConditions = ({
    actionType,
    locationIds,
    dayPart,
    dayOfWeek,
    weekOfYear,
    monthOfYear,
    primaryPlatform,
    amount,
    amountPredicate,
    scenario,
    channel,
    audienceIds,
    memberOfAudienceIds,
    notMemberOfAudienceIds,
    schemeId,
    perkId,
    voucherId,
    clientPlatforms,
    categoryIds,
    productIds,
    paymentMethod,
    emailEndsWith,
    favouriteLocationIds,
    // booking specific conditions, TODO: might require a change when the platform is ready
    arrivalDaysOfWeek,
    arrivalDateFrom,
    arrivalDateTo,
    departureDaysOfWeek,
    departureDateFrom,
    departureDateTo,
    stayLength,
    stayLengthPredicate,
    status,
    guestsNumber,
    guestsNumberPredicate,
    guestEmailEndsWith,
    bookerEmailEndsWith,
    rateCode,
    cost,
    costPredicate,
    // subscription specific conditions
    subscriptionSchemeId,
    stripePriceId,
    cancellationReason
}: IAudienceAddTriggersFormData) => {
    const conditions: Condition[] = [
        {
            subject: 'type',
            predicate: 'EQUAL',
            object: actionType
        }
    ];

    const finalData: Record<string, Condition | undefined> = {
        locationIds: locationIds
            ? createConditionForValueOrArray('context.location._id', locationIds)
            : undefined,
        dayPart: dayPart
            ? {
                  subject: 'when.daypart',
                  predicate: 'IN',
                  object: [dayPart]
              }
            : undefined,
        dayOfWeek: dayOfWeek
            ? {
                  subject: 'when.dayofweek',
                  predicate: 'EQUAL',
                  object: Number(dayOfWeek)
              }
            : undefined,
        weekOfYear: weekOfYear
            ? {
                  subject: 'when.weekofyear',
                  predicate: 'EQUAL',
                  object: Number(weekOfYear)
              }
            : undefined,
        monthOfYear: monthOfYear
            ? {
                  subject: 'when.monthofyear',
                  predicate: 'EQUAL',
                  object: Number(monthOfYear)
              }
            : undefined,
        primaryPlatform: primaryPlatform
            ? {
                  subject: 'context.user.primaryPlatform',
                  predicate: 'EQUAL',
                  object: primaryPlatform
              }
            : undefined,
        amount:
            amount && amountPredicate
                ? ({
                      subject: 'metadata.value.amount',
                      predicate: amountPredicate,
                      object: Number(amount)
                  } as Condition)
                : undefined,
        scenario: scenario
            ? {
                  subject: 'metadata.order.scenario',
                  predicate: 'EQUAL',
                  object: scenario
              }
            : undefined,
        channel: channel
            ? {
                  subject: 'metadata.channel',
                  predicate: 'EQUAL',
                  object: channel
              }
            : undefined,
        audienceIds: audienceIds
            ? createConditionForValueOrArray('metadata.audienceId', audienceIds)
            : undefined,
        schemeId: schemeId
            ? {
                  subject: 'metadata.schemeId',
                  predicate: 'EQUAL',
                  object: schemeId
              }
            : undefined,
        perkId: perkId
            ? {
                  subject: 'metadata.perkId',
                  predicate: 'EQUAL',
                  object: perkId
              }
            : undefined,
        voucherId: voucherId
            ? {
                  subject: 'metadata.voucherId',
                  predicate: 'EQUAL',
                  object: voucherId
              }
            : undefined,
        clientPlatforms: clientPlatforms
            ? createConditionForValueOrArray('context.clientPlatform', clientPlatforms)
            : undefined,
        categoryIds:
            categoryIds && categoryIds.length > 0
                ? {
                      subject: 'metadata.order.items',
                      predicate: 'HAS_CATEGORY_ID',
                      object: categoryIds
                  }
                : undefined,
        productIds:
            productIds && productIds.length > 0
                ? {
                      subject: 'metadata.order.items',
                      predicate: 'HAS_PRODUCT_ID',
                      object: productIds
                  }
                : undefined,
        paymentMethod: paymentMethod
            ? {
                  subject: 'metadata.order.payments',
                  predicate: 'HAS_PAYMENT_WITH_TYPE',
                  object: paymentMethod
              }
            : undefined,
        emailEndsWith: emailEndsWith
            ? {
                  subject: 'metadata.credential.id',
                  predicate: 'ENDS_WITH',
                  object: emailEndsWith
              }
            : undefined,
        // TODO: might require a change when the platform is ready
        arrivalDaysOfWeek:
            arrivalDaysOfWeek?.length > 0
                ? {
                      subject: 'WEEKDAY(metadata.arrival)',
                      predicate: 'IN',
                      object: arrivalDaysOfWeek.map(day => Number(day))
                  }
                : undefined,
        arrivalDateFrom: arrivalDateFrom
            ? {
                  subject: 'metadata.arrival',
                  predicate: 'GREATER',
                  object: format(arrivalDateFrom, 'yyyy-MM-dd')
              }
            : undefined,
        arrivalDateTo: arrivalDateTo
            ? {
                  subject: 'metadata.arrival',
                  predicate: 'LESSER',
                  object: format(arrivalDateTo, 'yyyy-MM-dd')
              }
            : undefined,
        departureDateFrom: departureDateFrom
            ? {
                  subject: 'metadata.departure',
                  predicate: 'GREATER',
                  object: format(departureDateFrom, 'yyyy-MM-dd')
              }
            : undefined,
        departureDateTo: departureDateTo
            ? {
                  subject: 'metadata.departure',
                  predicate: 'LESSER',
                  object: format(departureDateTo, 'yyyy-MM-dd')
              }
            : undefined,
        departureDaysOfWeek:
            departureDaysOfWeek.length > 0
                ? {
                      subject: 'WEEKDAY(metadata.departure)',
                      predicate: 'IN',
                      object: departureDaysOfWeek.map(day => Number(day))
                  }
                : undefined,
        stayLength: stayLengthPredicate
            ? ({
                  subject: 'metadata.lengthOfStayNights',
                  predicate: stayLengthPredicate,
                  object: Number(stayLength)
              } as Condition)
            : undefined,
        guestsNumber: guestsNumberPredicate
            ? ({
                  subject: 'LEN(metadata.guests)',
                  predicate: guestsNumberPredicate,
                  object: Number(guestsNumber)
              } as Condition)
            : undefined,
        guestEmailEndsWith: guestEmailEndsWith
            ? {
                  subject: 'ANY(metadata.guests.email)',
                  predicate: 'ENDS_WITH',
                  object: guestEmailEndsWith
              }
            : undefined,
        bookerEmailEndsWith: bookerEmailEndsWith
            ? {
                  subject: 'ANY(metadata.booker.email)',
                  predicate: 'ENDS_WITH',
                  object: bookerEmailEndsWith
              }
            : undefined,
        status: status
            ? {
                  subject: 'metadata.status',
                  predicate: 'EQUAL',
                  object: status
              }
            : undefined,
        rateCode: rateCode
            ? {
                  subject: 'metadata.ratePlan',
                  predicate: 'EQUAL',
                  object: rateCode
              }
            : undefined,
        cost:
            cost && costPredicate
                ? {
                      subject: 'metadata.cost',
                      predicate: costPredicate,
                      object: Number(cost)
                  }
                : undefined,
        favouriteLocationIds: getFavouriteLocationsCondition(favouriteLocationIds),
        // subscription specific conditions (cast via unknown as these subjects are not yet in types-sdk)
        subscriptionSchemeId: subscriptionSchemeId
            ? ({
                  subject: 'metadata.subscriptionSchemeId',
                  predicate: 'EQUAL',
                  object: subscriptionSchemeId
              } as unknown as Condition)
            : undefined,
        stripePriceId: stripePriceId
            ? ({
                  subject: 'metadata.stripe.priceId',
                  predicate: 'EQUAL',
                  object: stripePriceId
              } as unknown as Condition)
            : undefined,
        cancellationReason: cancellationReason
            ? ({
                  subject: 'metadata.cancellationReason',
                  predicate: 'EQUAL',
                  object: cancellationReason
              } as unknown as Condition)
            : undefined
    };

    const pushConditions = (keys: string[]) => {
        keys.forEach(key => {
            if (!!finalData[key]) {
                conditions.push(finalData[key]);
            }
        });
    };

    // Common to all actions except subscription events
    if (
        getActionTypeGroup(actionType) !== EActionTypeGroup.FIFTEENTH &&
        getActionTypeGroup(actionType) !== EActionTypeGroup.SIXTEENTH
    ) {
        pushConditions(['clientPlatforms']);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.FIRST) {
        pushConditions([
            'locationIds',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform'
        ]);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.SECOND) {
        pushConditions(['dayPart', 'dayOfWeek', 'weekOfYear', 'monthOfYear']);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.THIRD) {
        pushConditions([
            'amount',
            'locationIds',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform',
            'scenario'
        ]);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.FOURTH) {
        pushConditions([
            'amount',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform',
            'channel'
        ]);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.FIFTH) {
        pushConditions(['dayPart', 'dayOfWeek', 'weekOfYear', 'monthOfYear', 'primaryPlatform']);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.SIXTH) {
        pushConditions(['audienceIds']);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.SEVENTH) {
        pushConditions(['schemeId']);
    }

    // For REDEEM_PERK
    if (getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH) {
        pushConditions(['dayPart', 'dayOfWeek', 'weekOfYear', 'monthOfYear', 'perkId']);
    }

    // For VOUCHER_REDEEMED
    if (getActionTypeGroup(actionType) === EActionTypeGroup.NINTH) {
        pushConditions(['dayPart', 'dayOfWeek', 'weekOfYear', 'monthOfYear', 'voucherId']);
    }

    // For REWARD_EARNED
    if (getActionTypeGroup(actionType) === EActionTypeGroup.TENTH) {
        pushConditions([
            'locationIds',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform',
            'perkId'
        ]);
    }

    // For ORDER_COMPLETED and ORDER_USER_BILLED
    if (getActionTypeGroup(actionType) === EActionTypeGroup.ELEVENTH) {
        pushConditions([
            'categoryIds',
            'productIds',
            'paymentMethod',
            'amount',
            'locationIds',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform',
            'scenario'
        ]);
    }

    // For CREDENTIAL_VERIFIED
    if (getActionTypeGroup(actionType) === EActionTypeGroup.TWELFTH) {
        pushConditions([
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear',
            'primaryPlatform',
            'emailEndsWith'
        ]);
    }

    // For bookings, TODO: might require a change when the platform is ready
    if (getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH) {
        pushConditions([
            'locationIds',
            'cost',
            'arrivalDaysOfWeek',
            'departureDaysOfWeek',
            'arrivalDateFrom',
            'arrivalDateTo',
            'departureDateFrom',
            'departureDateTo',
            'stayLength',
            'stayLengthPredicate',
            'status',
            'guestsNumber',
            'guestsNumberPredicate',
            'emailEndsWith',
            'bookerEmailEndsWith',
            'guestEmailEndsWith',
            'rateCode'
        ]);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.FOURTEENTH) {
        pushConditions(['favouriteLocationIds']);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.FIFTEENTH) {
        pushConditions([
            'subscriptionSchemeId',
            'stripePriceId',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear'
        ]);
    }

    if (getActionTypeGroup(actionType) === EActionTypeGroup.SIXTEENTH) {
        pushConditions([
            'subscriptionSchemeId',
            'stripePriceId',
            'cancellationReason',
            'dayPart',
            'dayOfWeek',
            'weekOfYear',
            'monthOfYear'
        ]);
    }

    memberOfAudienceIds?.forEach(audienceId => {
        conditions.push({
            subject: 'context.user._id',
            predicate: 'IS_MEMBER_OF',
            object: audienceId
        });
    });

    notMemberOfAudienceIds?.forEach(audienceId => {
        conditions.push({
            subject: 'context.user._id',
            predicate: 'NOT_IS_MEMBER_OF',
            object: audienceId
        });
    });

    return conditions;
};
